import {
	IAuthStore,
	IAuthFormLogin,
	IAuth_Get_LoginData,
	IAuthFormResetRequest,
	IAuth_Get_ResetRequestData,
	IAuth_Get_ResetPasswordData,
	IAuth_Get_Jobs,
} from "./types";
import { formSelectors } from "../../_core/form";
import { jobSelectors } from "../../_core/job";
import actions from "./actions";
import { getType } from "typesafe-actions";
import { DEV_EMAIL } from "redux/_common/utils";
import { isVolteoEmail } from "utils/email";
const authSelectors = (authStore: IAuthStore) => {
	const isLoggedIn = () => (authStore.authToken ? true : false);
	const isLoggedOut = () => !isLoggedIn();
	const getLoggedInUser = () => authStore.user;
	const getLoginData = (): IAuth_Get_LoginData => {
		return {
			form: formSelectors(authStore._forms.LOGIN),
			loginJob: jobSelectors.getJobStatus(
				authStore,
				getType(actions.login)
			),
		};
	};
	const isPassFieldVisible = () => (authStore.isPassFieldVisible)
	const getResetRequestData = (): IAuth_Get_ResetRequestData => {
		return {
			form: formSelectors(authStore._forms.RESET_REQUEST),
			resetRequestJob: jobSelectors.getJobStatus(
				authStore,
				getType(actions.resetRequest)
			),
		};
	};
	const getResetPasswordData = (): IAuth_Get_ResetPasswordData => {
		return {
			form: formSelectors(authStore._forms.RESET_PASSWORD),
			resetPasswordJob: jobSelectors.getJobStatus(
				authStore,
				getType(actions.resetPassword)
			),
		};
	};

	const getAllJobs = (): IAuth_Get_Jobs => ({
		loginJob: jobSelectors.getJobStatus(authStore, getType(actions.login)),
		resetRequestJob: jobSelectors.getJobStatus(
			authStore,
			getType(actions.resetRequest)
		),
		resetPasswordJob: jobSelectors.getJobStatus(
			authStore,
			getType(actions.resetPassword)
		),
	});

	const allowedModulesForThirdParty = ["reports"];

	const allowedModulesForVessel = ["insights"];

	const isUserThirdParty = () => {
		const user = getLoggedInUser();
		if (user) {
			return user.roleCategory === "external";
		}
		return false;
	};

	const isUserAdmin = () => {
		const user = getLoggedInUser();
		if (!user) {
			return false;
		}

		const role = String(user.role || "").toLowerCase();
		if (role === "admin" || role === "superadmin") {
			return true;
		}

		return String(user.roleCategory || "").toLowerCase() === "admin";
	};

	const canViewUsersModule = () => {
		if (isUserAdmin()) {
			return true;
		}

		const user = getLoggedInUser();
		if (!user) {
			return false;
		}

		const roleCategory = String(user.roleCategory || "").toLowerCase();
		if (
			roleCategory === "developer" ||
			roleCategory === "support"
		) {
			return true;
		}

		const role = String(user.role || "").toLowerCase();
		return role === "support";
	};

	const getBaseAuthorisedModules = () => {
		const modules = ["dashboard", "vessel", "reports", "insights"];
		if (canViewUsersModule()) {
			modules.push("users");
		}
		return modules;
	};

	const isUserAllowedToUseCNRTool = () => {
		const user = getLoggedInUser();
		if (user) {
			return user.roleCategory === "admin";
		}
		return false;
	};

	const getAuthorisedModules = () => authStore?.authorisedModules;

	const getTenantInfo = () => authStore?.tenantInfo;

	const getTenantCode = () => {
		const tenantInfo = getTenantInfo();
		const tenantId = tenantInfo?._id;

		const tenantConstants: any = {
			"1076c7c10b69e86246f3910b62fc78f5": "TEEKAY",
			"434d9b50-5703-11eb-8d4a-6b516664bdbd": "EPS",
			"tenants::198fcd4c-c080-4412-a9fb-342bf02a0172":"VISWALABS"
		};
		if (tenantId) {
			if (tenantId in tenantConstants) {
				return tenantConstants[tenantId];
			}
			return tenantInfo?.name;
		}

		return null;
	};

	const isUserAllowedToAccessModule = (
		moduleName?: "feedback" | "tools" | "cnrReport" | string
	): boolean | null => {
		if (!moduleName) {
			return false;
		}
		const user = getLoggedInUser();
		if (user) {
			// if user email is DEV_EMAIL
			if (user.email === DEV_EMAIL) {
				return true;
			}

			// if user roleCategory is "vessel", only allow insights module
			if (user.roleCategory === "vessel") {
				return allowedModulesForVessel.includes(moduleName);
			}

			// if user roleCategory is "developer" or "support"
			if (
				user.roleCategory === "developer" ||
				user.roleCategory === "support" ||
				user.roleCategory === "admin"
			) {
				// the user's email must have the domain "volteo.com" or "volteomaritime.com"
				return isVolteoEmail(user.email);
			}
		}

		// console.log("returning null");
		// user is not present, so forcefully logout user by returning null

		return null;
	};

	const isUserVessel = () => {
		const user = getLoggedInUser();
		if (user) {
			return user.roleCategory === "vessel";
		}
		return false;
	};

	return {
		isLoggedIn,
		isLoggedOut,
		getLoggedInUser,
		isPassFieldVisible,
		getLoginData,
		getResetRequestData,
		getResetPasswordData,
		getAllJobs,
		isUserAllowedToAccessModule,
		getAuthorisedModules,
		getBaseAuthorisedModules,
		getTenantInfo,
		getTenantCode,
		isUserAdmin,
		canViewUsersModule,
		isUserThirdParty,
		allowedModulesForThirdParty,
		isUserAllowedToUseCNRTool,
		isUserVessel,
		allowedModulesForVessel,
	};
};

export default authSelectors;
